import React, {useRef, useEffect, useState} from 'react'
import {Box, IconButton} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import Hls from 'hls.js'
import {
    Play as PlayIcon,
    Pause as PauseIcon,
    ProductVideoMuteIcon,
    ProductVideoSoundIcon
} from '../icons'
import {useMultiStyleConfig} from '@chakra-ui/react'
import {
    AUDIO_STATUS,
    AUDIO_PROBE_MAX_BYTES,
    detectAudibleSignal,
    detectMp4AudioStatus,
    getCachedAudioStatus,
    getHlsBufferAudioStatus,
    getHlsManifestAudioStatus,
    getMediaElementAudioStatus,
    setCachedAudioStatus
} from '../../helpers/audio-detection'

const isMp4Source = (src) => /\.mp4(\?.*)?$/i.test(src) || /edge\.curalate\.com\/v1\/vid\//i.test(src)

/**
 * UGC video player, shared by the landing tile and the expanded view.
 *
 * mode='hover' (landing tile): starts paused; hover plays it until the user
 * clicks, then their choice is locked. Always muted, no sound button.
 *
 * mode='autoplay' (expanded view): plays and loops on mount, ignores hover.
 * Shows a mute button when the video has sound; mute is controlled by the
 * parent so all videos stay in sync.
 *
 * Both modes pause off-screen and resume on-screen.
 */
const UgcVideoPlayer = ({
    src,
    poster,
    mode = 'hover',
    isHovered = false,
    showSoundControl = false,
    muted,
    onMuteChange,
    onReady,
    onError
}) => {
    const styles = useMultiStyleConfig('UgcVideoPlayer', {})
    const intl = useIntl()
    const isAutoplayMode = mode === 'autoplay'

    const [isPlaying, setIsPlaying] = useState(isAutoplayMode)
    const [hasError, setHasError] = useState(false)
    const [audioStatus, setAudioStatus] = useState(() =>
        showSoundControl ? getCachedAudioStatus(src) : AUDIO_STATUS.ABSENT
    )

    // Sound is never exposed in hover mode — always muted, regardless of any
    // `muted` prop passed in by mistake.
    const effectiveMuted = showSoundControl ? muted ?? true : true

    const effectiveMutedRef = useRef(effectiveMuted)

    const formatLabel = (descriptor) => {
        const label = intl.formatMessage(descriptor)
        return typeof label === 'string' ? label : descriptor.defaultMessage
    }

    const containerRef = useRef(null)
    const videoRef = useRef(null)
    const hlsRef = useRef(null)
    const observerRef = useRef(null)
    // hover mode: null = hover drives playback, true/false = user-locked choice.
    const manualOverrideRef = useRef(null)
    const isHoveredRef = useRef(isHovered)
    // autoplay mode: whether the user has explicitly paused.
    const userPausedRef = useRef(false)
    const unmuteInFlightRef = useRef(false)
    const isInViewportRef = useRef(true)
    const isReadyRef = useRef(false)
    const srcRef = useRef(src)
    const isNativeHlsRef = useRef(false)
    const hasStartedLoadRef = useRef(false)
    const audioStatusRef = useRef(audioStatus)
    const audioInitSegmentRef = useRef(null)
    const audioProbeAttemptsRef = useRef(0)
    const audioProbeInFlightRef = useRef(false)
    const AUDIO_PROBE_MAX_ATTEMPTS = 4

    const notifyError = () => {
        setHasError(true)
        onError?.()
    }

    const maybeStartLoad = () => {
        if (Hls.isSupported() && hlsRef.current && !hasStartedLoadRef.current) {
            hlsRef.current.startLoad(-1)
            hasStartedLoadRef.current = true
        }
    }

    const updateAudioStatus = (status, {cache = true} = {}) => {
        if (!showSoundControl) return
        if (
            status === AUDIO_STATUS.UNKNOWN ||
            (audioStatusRef.current === AUDIO_STATUS.PRESENT && status === AUDIO_STATUS.ABSENT)
        ) {
            return
        }
        audioStatusRef.current = status
        if (cache) setCachedAudioStatus(srcRef.current, status)
        setAudioStatus(status)
    }

    const updateAudioStatusFromMedia = () => {
        if (!showSoundControl || hlsRef.current) return
        updateAudioStatus(getMediaElementAudioStatus(videoRef.current), {cache: false})
    }

    // What playback SHOULD be right now, per the active mode.
    const desiredPlaying = () =>
        isAutoplayMode
            ? !userPausedRef.current
            : manualOverrideRef.current != null
            ? manualOverrideRef.current
            : isHoveredRef.current

    // Always start muted — autoplay is permitted on every browser (Safari
    // included) — then restore the shopper's sound once it is actually playing.
    // If un-muting pauses the element (Safari), the pause handler reverts to
    // muted playback so a swipe never leaves the video stalled.
    const startPlayback = (video) => {
        const wantSound = !effectiveMutedRef.current
        unmuteInFlightRef.current = false
        video.muted = true
        const playPromise = video.play()
        playPromise
            ?.then(() => {
                if (wantSound && !userPausedRef.current) {
                    unmuteInFlightRef.current = true
                    video.muted = false
                }
            })
            ?.catch(() => {})
    }

    const applyPlayback = () => {
        const video = videoRef.current
        if (!video || !isReadyRef.current) return
        if (desiredPlaying() && isInViewportRef.current) {
            maybeStartLoad()
            startPlayback(video)
        } else {
            video.pause()
        }
    }

    // Hover-mode: react to hover prop changes, only while unlocked.
    useEffect(() => {
        if (isAutoplayMode) return
        isHoveredRef.current = isHovered
        if (manualOverrideRef.current == null) applyPlayback()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isHovered])

    // Keep the video element's mute attribute in sync with the controlled prop.
    useEffect(() => {
        effectiveMutedRef.current = effectiveMuted
        if (videoRef.current) videoRef.current.muted = effectiveMuted
    }, [effectiveMuted])

    // Safari pauses a video when an autoplay-started element is un-muted without
    // a gesture. Only that pause is reverted — back to muted playback — so the
    // swipe still autoplays. User pauses and off-screen pauses are left alone.
    useEffect(() => {
        if (!isAutoplayMode) return undefined
        const video = videoRef.current
        if (!video) return undefined
        const handlePause = () => {
            if (!unmuteInFlightRef.current || userPausedRef.current || !isInViewportRef.current) {
                return
            }
            unmuteInFlightRef.current = false
            video.muted = true
            video.play().catch(() => {})
        }
        video.addEventListener('pause', handlePause)
        return () => video.removeEventListener('pause', handlePause)
    }, [isAutoplayMode])

    useEffect(() => {
        if (!showSoundControl || !isMp4Source(src)) return
        const controller = new AbortController()
        detectMp4AudioStatus(src, controller.signal).then((status) => {
            if (!controller.signal.aborted && src === srcRef.current) updateAudioStatus(status)
        })
        return () => controller.abort()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src, showSoundControl])

    // Source swap (e.g. clicking a different look in "More Looks").
    useEffect(() => {
        if (!videoRef.current || !src) return

        srcRef.current = src
        isReadyRef.current = false
        hasStartedLoadRef.current = false
        setHasError(false)
        const cachedStatus = showSoundControl ? getCachedAudioStatus(src) : AUDIO_STATUS.ABSENT
        audioStatusRef.current = cachedStatus
        setAudioStatus(cachedStatus)
        audioInitSegmentRef.current = null
        audioProbeAttemptsRef.current = 0
        audioProbeInFlightRef.current = false
        userPausedRef.current = false
        manualOverrideRef.current = null
        setIsPlaying(isAutoplayMode)

        const isMp4 = isMp4Source(src)

        if (isMp4) {
            videoRef.current.src = src
            videoRef.current.load()
            videoRef.current.addEventListener(
                'loadedmetadata',
                () => {
                    isReadyRef.current = true
                    updateAudioStatusFromMedia()
                    applyPlayback()
                },
                {once: true}
            )
        } else if (Hls.isSupported() && hlsRef.current) {
            hlsRef.current.loadSource(src)
            maybeStartLoad()
            hlsRef.current.once(Hls.Events.MANIFEST_PARSED, (_, data) => {
                isReadyRef.current = true
                updateAudioStatus(getHlsManifestAudioStatus(data))
                applyPlayback()
            })
        } else if (isNativeHlsRef.current) {
            videoRef.current.src = src
            videoRef.current.load()
            videoRef.current.addEventListener(
                'loadedmetadata',
                () => {
                    isReadyRef.current = true
                    updateAudioStatusFromMedia()
                    applyPlayback()
                },
                {once: true}
            )
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src])

    // One-time setup: source load, HLS, viewport observer.
    useEffect(() => {
        let mounted = true
        const videoEl = videoRef.current
        if (!videoEl || !containerRef.current) return

        videoEl.muted = effectiveMuted
        videoEl.setAttribute('playsinline', '')
        videoEl.setAttribute('webkit-playsinline', '')
        if (typeof poster === 'string' && poster) {
            videoEl.poster = poster.replace(/\[.*?\]$/, '')
        }

        const useNativeHls = !Hls.isSupported() && !!videoEl.canPlayType('application/vnd.apple.mpegurl')
        isNativeHlsRef.current = useNativeHls
        const isMp4 = isMp4Source(src)

        if (isMp4) {
            videoEl.src = src
            videoEl.addEventListener(
                'loadedmetadata',
                () => {
                    if (!mounted) return
                    isReadyRef.current = true
                    updateAudioStatusFromMedia()
                    applyPlayback()
                    onReady?.()
                },
                {once: true}
            )
        } else if (Hls.isSupported()) {
            const hls = new Hls({autoStartLoad: false, startLevel: -1, enableWorker: true})
            hls.loadSource(src)
            hls.attachMedia(videoEl)

            hls.on(Hls.Events.MANIFEST_PARSED, (_, data) => {
                if (!mounted) return
                isReadyRef.current = true
                updateAudioStatus(getHlsManifestAudioStatus(data))
                maybeStartLoad()
                applyPlayback()
                onReady?.()
            })

            // Audio-track probing is only wired up when sound is actually
            // exposed — a grid tile never runs any of this.
            if (showSoundControl) {
                hls.on(Hls.Events.BUFFER_CODECS, (_, data) => {
                    if (!mounted) return
                    updateAudioStatus(getHlsBufferAudioStatus(data))
                })

                hls.on(Hls.Events.BUFFER_APPENDING, async (_, data) => {
                    if (
                        !mounted ||
                        audioStatusRef.current === AUDIO_STATUS.PRESENT ||
                        audioProbeInFlightRef.current ||
                        audioProbeAttemptsRef.current >= AUDIO_PROBE_MAX_ATTEMPTS ||
                        !['audio', 'audiovideo'].includes(data?.type) ||
                        !data?.data?.byteLength
                    ) {
                        return
                    }
                    const chunk = new Uint8Array(data.data)
                    const bounded =
                        chunk.byteLength > AUDIO_PROBE_MAX_BYTES
                            ? chunk.slice(0, AUDIO_PROBE_MAX_BYTES)
                            : chunk.slice()

                    if (!audioInitSegmentRef.current) {
                        audioInitSegmentRef.current = bounded
                        return
                    }
                    const initSeg = audioInitSegmentRef.current
                    const remaining = AUDIO_PROBE_MAX_BYTES - initSeg.byteLength
                    if (remaining <= 0) return
                    const mediaSeg = bounded.byteLength > remaining ? bounded.slice(0, remaining) : bounded
                    const combined = new Uint8Array(initSeg.byteLength + mediaSeg.byteLength)
                    combined.set(initSeg)
                    combined.set(mediaSeg, initSeg.byteLength)

                    audioProbeInFlightRef.current = true
                    audioProbeAttemptsRef.current += 1
                    const probeSrc = srcRef.current
                    const status = await detectAudibleSignal(combined.buffer, probeSrc)
                    audioProbeInFlightRef.current = false
                    if (!mounted || probeSrc !== srcRef.current) return

                    if (status === AUDIO_STATUS.PRESENT) {
                        updateAudioStatus(status)
                    } else if (
                        status === AUDIO_STATUS.ABSENT &&
                        audioProbeAttemptsRef.current >= AUDIO_PROBE_MAX_ATTEMPTS
                    ) {
                        updateAudioStatus(status)
                    }
                })
            }

            hls.on(Hls.Events.ERROR, (_, data) => {
                if (!mounted || !data?.fatal) return
                switch (data.type) {
                    case Hls.ErrorTypes.NETWORK_ERROR:
                        hls.destroy()
                        notifyError()
                        break
                    case Hls.ErrorTypes.MEDIA_ERROR:
                        hls.recoverMediaError()
                        break
                    default:
                        hls.destroy()
                        notifyError()
                        break
                }
            })

            hlsRef.current = hls
        } else if (useNativeHls) {
            videoEl.src = src
            videoEl.addEventListener(
                'loadedmetadata',
                () => {
                    if (!mounted) return
                    isReadyRef.current = true
                    updateAudioStatusFromMedia()
                    applyPlayback()
                    onReady?.()
                },
                {once: true}
            )
        }

        observerRef.current = new IntersectionObserver(
            ([entry]) => {
                isInViewportRef.current = entry.isIntersecting
                applyPlayback()
            },
            {threshold: 0.2}
        )
        observerRef.current.observe(containerRef.current)

        return () => {
            mounted = false
            observerRef.current?.disconnect()
            observerRef.current = null
            hlsRef.current?.destroy()
            hlsRef.current = null
            if (videoRef.current) {
                videoRef.current.pause()
                videoRef.current.removeAttribute('src')
                videoRef.current.load()
            }
            isReadyRef.current = false
            hasStartedLoadRef.current = false
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handlePlayPause = (e) => {
        e.preventDefault()
        e.stopPropagation()
        if (!videoRef.current) return

        if (isAutoplayMode) {
            userPausedRef.current = !videoRef.current.paused
        } else {
            // Click hands FULL, PERMANENT control to the user — hover is
            // never consulted again after this point for this instance.
            manualOverrideRef.current = videoRef.current.paused
        }
        applyPlayback()
    }

    const handleMuteToggle = (e) => {
        e.preventDefault()
        e.stopPropagation()
        const nextMuted = !effectiveMuted
        onMuteChange?.(nextMuted)
        if (!nextMuted) videoRef.current?.play().catch(() => {})
    }

    if (hasError) return null

    return (
        <Box ref={containerRef} {...styles.container} data-testid="ugc-video-player">
            <Box {...styles.videoWrapper}>
                <Box
                    as="video"
                    ref={videoRef}
                    loop
                    autoPlay={isAutoplayMode}
                    playsInline
                    onPlaying={updateAudioStatusFromMedia}
                    onTimeUpdate={updateAudioStatusFromMedia}
                    onError={notifyError}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    {...styles.video}
                />
            </Box>
            <Box {...styles.controlsWrapper}>
                <IconButton
                    {...styles.iconButton}
                    onClick={handlePlayPause}
                    data-testid="ugc-video-play-pause-button"
                    aria-label={
                        isPlaying
                            ? formatLabel({id: 'ugc_video_player.pause', defaultMessage: 'Pause'})
                            : formatLabel({id: 'ugc_video_player.play', defaultMessage: 'Play'})
                    }
                    icon={isPlaying ? <PauseIcon {...styles.iconSize} /> : <PlayIcon {...styles.iconSize} />}
                />
                {showSoundControl && audioStatus === AUDIO_STATUS.PRESENT && (
                    <IconButton
                        {...styles.iconButton}
                        onClick={handleMuteToggle}
                        data-testid="ugc-video-mute-button"
                        aria-label={
                            effectiveMuted
                                ? formatLabel({id: 'ugc_video_player.unmute', defaultMessage: 'Unmute'})
                                : formatLabel({id: 'ugc_video_player.mute', defaultMessage: 'Mute'})
                        }
                        icon={
                            // The sprite's `Mute`/`Unmute` symbols have swapped
                            // artwork (mute.svg draws the sound waves, unmute.svg
                            // the X), which made the muted state look like
                            // sound-on. This pair is named correctly:
                            // muted = speaker + X, unmuted = speaker + waves.
                            effectiveMuted ? (
                                <ProductVideoMuteIcon {...styles.iconSize} />
                            ) : (
                                <ProductVideoSoundIcon {...styles.iconSize} />
                            )
                        }
                    />
                )}
            </Box>
        </Box>
    )
}

UgcVideoPlayer.propTypes = {
    src: PropTypes.string.isRequired,
    poster: PropTypes.string,
    mode: PropTypes.oneOf(['hover', 'autoplay']),
    isHovered: PropTypes.bool,
    showSoundControl: PropTypes.bool,
    muted: PropTypes.bool,
    onMuteChange: PropTypes.func,
    onReady: PropTypes.func,
    onError: PropTypes.func
}

export default UgcVideoPlayer